import { toast } from 'sonner';
import type { Bookmark } from '@stashmark/shared';
import { extractDomain } from '@stashmark/shared';
import { useDeleteBookmarkMutation } from '@/api/bookmarks';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

type Props = {
  bookmark: Bookmark | null;
  isOpen: boolean;
  onClose: () => void;
};

export const DeleteConfirmDialog = ({ bookmark, isOpen, onClose }: Props) => {
  const [deleteBookmark, { isLoading }] = useDeleteBookmarkMutation();

  const handleDelete = async () => {
    if (!bookmark) {
      return;
    }

    try {
      await deleteBookmark(bookmark.id).unwrap();
      toast.success('Bookmark deleted successfully');
      onClose();
    } catch (err) {
      toast.error('Failed to delete bookmark', {
        description: err instanceof Error ? err.message : 'Please try again',
      });
    }
  };

  if (!bookmark) {
    return null;
  }

  const displayName = bookmark.title ?? extractDomain(bookmark.url) ?? bookmark.url;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Bookmark</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete &quot;{displayName}&quot;? This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
